import React, { useState } from 'react';
import BurgerMenuIcon from './BurgerMenuIcon';
import { Box, Container, IconButton, Stack, Typography, CircularProgress } from '@mui/material'; // Added Button
import { Link, useNavigate } from 'react-router-dom';
import { theme, themeColors } from '../styles/theme';
import Fonts from './Fonts';
import CloseIcon from "@mui/icons-material/Close";
import NearMeIcon from '@mui/icons-material/NearMe';
import PersonIcon from '@mui/icons-material/Person';
import PubSearchGoogle, { GooglePubResult } from './PubSearchGoogle';
import { checkPubSupport } from '../utils/checkPubSupport';
import { useAuth } from '../AuthProvider';
import LoginModal from './LoginModal';
import ProfileModal from './ProfileModal';
import { trackHomePageExit, trackPubNotFound } from '../utils/analytics';
import suntapLogo from '../img/suntap_logo.svg';
import sunIcon from '../img/sun_icon.png';
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useEffect } from 'react';
import suntapIllustrationLogo from "../img/suntap_illustration_logo.svg";

interface TopNavProps {
    title: string;
}

export const HEADER_HEIGHT = '10vh';
const MENU_ITEM_STYLE = {
    marginTop: '12px',
    padding: '12px',
    textAlign: 'center',
    borderRadius: '40px',
    color: themeColors.darkBackground,
    fontFamily: "ApfelGrotezkBold",
    fontSize: '1.6rem',
    fontWeight: 700,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateX(-4px)',
    }
}

const TopNav = ({
    title, // title is passed but not used in JSX
}: TopNavProps) => {
    const navigate = useNavigate();
    const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
    
    const [searchError, setSearchError] = useState<string | React.ReactNode | null>(null);
    const [searchLoading, setSearchLoading] = useState(false); // Renamed to avoid conflict
    const [searchLoadingMessage, setSearchLoadingMessage] = useState<string | null>(null);

    const { user, logout, loading: authLoading, isAnonymous, profile } = useAuth();
    const [loginModalOpen, setLoginModalOpen] = useState<boolean>(false);
    const [profileModalOpen, setProfileModalOpen] = useState<boolean>(false); // State for ProfileModal
    const [isStaff, setIsStaff] = useState(false);

    useEffect(() => {
        setIsStaff(!!profile?.is_staff);
    }, [profile]);

    const handleLogout = async () => {
        await logout();
        setBurgerMenuOpen(false); // Close menu on logout
    }

    const handleAuthAction = () => {
        setBurgerMenuOpen(false); // Close menu first
        if (user && !isAnonymous) { // If logged-in (not anonymous)
            setProfileModalOpen(true); // Open profile modal
        } else { // If anonymous or no user
            setLoginModalOpen(true); // Open login modal
        }
    };
    
    // Derived state for auth button text
    let authButtonText = 'Sign In';
    if (!authLoading) {
        if (user && !isAnonymous) {
            authButtonText = profile?.display_name || user.email || 'MY ACCOUNT';
        }
    }


    // This function also becomes very clean
    const handlePubSelect = async (selectedPub: GooglePubResult) => {
        try {
            setSearchLoading(true);
            setSearchError(null);
            
            // Use the same helper function here
            const pubToNavigate = await checkPubSupport(
                selectedPub,
                setSearchLoadingMessage
            );

            // If it succeeds, navigate.
            setBurgerMenuOpen(false);
            trackHomePageExit(`pub_${pubToNavigate.google_place_id}`);
            navigate(`/pub/${pubToNavigate.google_place_id}`, {
                state: { pubToView: pubToNavigate }
            });

        } catch (error: any) {
            // Handle errors gracefully
            console.error('Error processing pub in TopNav:', error);
            trackPubNotFound(selectedPub.name || "pub");
            setSearchError(error.message);
        } finally {
            setSearchLoading(false);
            setSearchLoadingMessage(null);
        }
    };

    return (
        <Box
            sx={{
                top: 0,
                width: '100%',
                color: '#FBE858',
                height: HEADER_HEIGHT,
                position: 'relative',
            }}
        >
        <Fonts />
        <Box
            sx={{
                top: '0',
                marginTop: '10px',
                width: '90%',
                height: HEADER_HEIGHT,
                background: '#FBE858',
                position: 'fixed',
                zIndex: 1000,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '30px',
                left: '50%',
                transform: 'translateX(-50%)'
            }}
        >
            <Container maxWidth="lg" sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                height: '100%',
                position: 'relative' 
            }}>
                <Link to="/" style={{ textDecoration: 'none' }} onClick={() => setBurgerMenuOpen(false)}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <img
                            src={suntapLogo}
                            alt="Suntap Logo"
                            style={{
                                height: '50px',
                            }}
                        />
                    </Box>
                </Link>
                <Stack 
                    direction={'row'} 
                    sx={{ 
                        alignItems: 'center',
                        height: '100%'
                    }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        mr: 1
                    }}>
                        <img
                            src={suntapIllustrationLogo}
                            alt="Sun Icon"
                            style={{
                                height: '35px',
                            }}
                        />
                    </Box>
                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="menu"
                        onClick={() => {
                            setBurgerMenuOpen(!burgerMenuOpen)
                        }}
                        sx={{ color: 'black', pl: '4' }}
                    >
                        {burgerMenuOpen ? <CloseIcon
                            sx={{
                                width: '30px',
                                height: '30px',
                                borderRadius: '50%',
                                border: `1px solid ${themeColors.darkBackground}`,
                                mx: '0'
                            }}
                        /> : <BurgerMenuIcon />}
                    </IconButton>
                </Stack>
            </Container>
        </Box>

        {burgerMenuOpen && (
        <Box
            className="menu-bar"
            sx={{
                position: 'fixed',
                // top: `calc(${HEADER_HEIGHT} + 30px)`, // Position below the fixed header + margin
                height: '100%', // Adjust height
                width: '100%',
                background: 'linear-gradient(to bottom, #FBE858 0%, #F8DAEE 100%)',
                zIndex: 900,
                padding: '24px',
                pb: '0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'left',
                borderRadius: '20px',
                overflowY: 'auto', // Allow scrolling if content overflows
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out', // Smooth transition
            }}
        >
            
            <Box sx={{ width: '100%', mb: 2, display: 'flex', justifyContent: 'center', mt: `calc(${HEADER_HEIGHT} + 20px)` }}>
                <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'left' }}>
                    <PubSearchGoogle
                        onPubSelect={handlePubSelect}
                        placeholder="Search for a pub"
                        onError={(err) => setSearchError(typeof err === 'string' ? err : 'Search error')}
                        containerSx={{
                            width: '100%',
                            maxWidth: '400px', // Match the input maxWidth
                            display: 'flex',
                            justifyContent: 'left'
                        }}
                        inputSx={{
                            borderRadius: '40px',
                            fontFamily: "ApfelGrotezkRegular",
                            backgroundColor: theme.palette.background.paper,
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 'none',
                            },
                            '.MuiOutlinedInput-input': {
                                fontFamily: "ApfelGrotezkRegular",
                                color: themeColors.darkBackground,
                            },
                            width: '100%', // Limit input width
                            minWidth: '300px',
                            display: 'flex',
                        }}
                    />
                    {searchLoading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                            <CircularProgress size={24} sx={{color: themeColors.orange}}/>
                            {searchLoadingMessage &&
                                <Typography sx={{ mt: 1, p: 1, fontSize: '0.9rem', textAlign: 'center', color: themeColors.darkBackground, fontFamily: "ApfelGrotezkRegular", fontWeight: 500 }}>
                                    {searchLoadingMessage}
                                </Typography>
                            }
                        </Box>
                    )}
                    {searchError && (
                        <Typography 
                            color="error" 
                            sx={{ mt: 1, fontSize: '0.8rem', textAlign: 'center' }}
                        >
                            {searchError}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Link to="/pubs" style={{ textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'left' }} onClick={() => setBurgerMenuOpen(false)}>
                <Stack direction={'row'} alignItems={'center'}>
                    <NearMeIcon sx={{ color: themeColors.darkBackground, fontSize: '2rem', transform: 'rotate(180deg)' }} />
                    <Typography variant="h5" sx={MENU_ITEM_STYLE}>Nearby sunny pubs</Typography>
                </Stack>
            </Link>
            <Link to="/search-area" style={{ textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'left' }} onClick={() => setBurgerMenuOpen(false)}>
                <Stack direction={'row'} alignItems={'center'}>
                    <LocationOnIcon sx={{ color: themeColors.darkBackground, fontSize: '2rem' }} />
                    <Typography variant="h5" sx={MENU_ITEM_STYLE}>Search an area</Typography>
                </Stack>
            </Link>
            {/*<Link to="/about" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>*/}
            {/*    <Typography variant="h5" sx={MENU_ITEM_STYLE}>About</Typography>*/}
            {/*</Link>*/}
            {/*<Link to="/submit" style={{ textDecoration: 'none', width: '100%' }} onClick={() => setBurgerMenuOpen(false)}>*/}
            {/*    <Typography variant="h5" sx={MENU_ITEM_STYLE}>Submit garden</Typography>*/}
            {/*</Link>*/}

            {isStaff && (
                <Link to="/moderate" style={{ textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'left' }} onClick={() => setBurgerMenuOpen(false)}>
                    <Typography variant="h5" sx={MENU_ITEM_STYLE}>Moderate</Typography>
                </Link>
            )}
            
            {/* Auth Action Button */}
            <Box 
                onClick={handleAuthAction}
            >
                {authLoading ? (
                    <CircularProgress size={20} sx={{color: themeColors.darkBackground}} />
                ) : (
                    <Stack direction={'row'} alignItems={'center'}>
                        <PersonIcon sx={{ color: themeColors.darkBackground, fontSize: '2rem', mt: 1 }} />
                        <Typography variant="h5" component="span" sx={MENU_ITEM_STYLE}>
                           {authButtonText}
                        </Typography>
                    </Stack>
                )}
            </Box>
            {/* Logout button specifically for logged-in users, if preferred over combined button */}
            {user && !isAnonymous && !authLoading && (
                 <Box 
                    onClick={handleLogout} 
                    sx={{ ...MENU_ITEM_STYLE, width: '100%', cursor: 'pointer', color: themeColors.orange, '&:hover': { color: themeColors.darkBackground, transform: 'translateX(-4px)'} }}
                >
                     <Typography variant="h5" component="span" sx={{fontSize: '1.2rem', fontWeight: 700}}>
                        Logout
                    </Typography>
                </Box>
            )}

            <Link to="/" style={{ textDecoration: 'none', width: '100%', display: 'flex', justifyContent: 'left' }} onClick={() => setBurgerMenuOpen(false)}>
                <Typography variant="h5" sx={{...MENU_ITEM_STYLE, pl: '4px'}}>Back to home</Typography>
            </Link>
        </Box>
        )}

        <LoginModal 
            open={loginModalOpen}
            onClose={() => setLoginModalOpen(false)}
        />
        <ProfileModal
            open={profileModalOpen}
            onClose={() => setProfileModalOpen(false)}
        />
        </Box>
    )
}

export default TopNav;